/**
 * ePlant Dashboard - Production Ready Release
 * 
 * Layout: Chart → Controls → Serial Table (Developer) | Chart → Serial Table (Public) → Map (both modes)
 * Mode Selector: Header on desktop, bottom of page on mobile
 * 
 * Features:
 * - Working tooltips with correct mouse position tracking
 * - All graph rendering and interactions functioning properly
 * - Developer mode: Chart at top, correction controls below, serial table below that, map at bottom
 * - Public mode: Chart at top, serial/status table, map at bottom
 * - Responsive design with proper spacing and alignment
 * - Mode selector repositioned to bottom of page for mobile (both modes)
 * - Mobile table list view: Checkbox, plant name, and traffic light aligned on one line with text truncation
 * - Mobile public mode graph: Properly constrained within viewport
 * - Desktop x-axis date labels: Dynamically calculated to show max 5 dates across entire range (both modes)
 * - Mobile x-axis date labels: Every 3 days for readability
 * 
 * Known Issues (minor, deferred to future commits):
 * (1) Mobile correction mode display (Developer mode): Control group layout needs refinement
 * 
 * This is looking amazing. New status quo - use as basis for all future development.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import './App.css';
import { BrowserRouter as Router, Route, Routes, useSearchParams } from 'react-router-dom';
import Landing from './Landing';
import ClipInstall from './ClipInstall';
import ContactUs from './ContactUs';
import AboutUs from './AboutUs';
import DendrometerFundamentals from './DendrometerFundamentals';
import GoogleMapWithPlant from './GoogleMapWithPlant';
import { API_BASE_URL } from './config';
import PrivacyPolicy from './PrivacyPolicy';
import TermsConditions from './TermsConditions';
import zoomPlugin from 'chartjs-plugin-zoom';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Custom plugin for vertical day background bars
const dailyBackgroundPlugin = (isMobile) => ({
  id: 'dailyBackgroundPlugin',
  beforeDatasetsDraw: (chart) => {
    const {ctx, chartArea, scales} = chart;
    if (!chartArea) return;
    const xScale = scales.x;
    if (!xScale) return;
    const labels = chart.data.labels || [];
    if (!labels || labels.length === 0) return;

    // Group label indices by date string (YYYY-MM-DD)
    const dateGroups = {};
    labels.forEach((label, idx) => {
      const ts = typeof label === 'number' ? label : Number(label);
      if (Number.isNaN(ts)) return;
      const d = new Date(ts);
      if (isNaN(d)) return;
      const dateStr = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
      if (!dateGroups[dateStr]) dateGroups[dateStr] = [];
      dateGroups[dateStr].push(idx);
    });
    const uniqueDates = Object.keys(dateGroups).sort();

    let colorToggle = false;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = 'bold 12px sans-serif';
    
    // Calculate label frequency for desktop to show max ~5 dates across entire range
    let labelFrequency;
    if (isMobile) {
      labelFrequency = 3; // Mobile: every 3 days
    } else {
      // Desktop: divide total date range to show max 5 dates
      labelFrequency = Math.max(1, Math.ceil(uniqueDates.length / 5));
    }
    
    uniqueDates.forEach((dateStr, dateIdx) => {
      const idxArray = dateGroups[dateStr];
      if (!idxArray || idxArray.length === 0) return;
      const firstIdx = Math.min(...idxArray);
      const lastIdx = Math.max(...idxArray);
      const xStart = xScale.getPixelForValue(firstIdx);
      const xEnd = xScale.getPixelForValue(lastIdx);
      ctx.fillStyle = colorToggle ? 'rgba(220, 200, 160, 0.35)' : 'rgba(255, 255, 255, 0.02)';
      ctx.fillRect(xStart, chartArea.top, xEnd - xStart, chartArea.bottom - chartArea.top);
      // Show date label based on calculated frequency
      if (dateIdx % labelFrequency === 0) {
        const xMid = xStart + (xEnd - xStart) / 2;
        ctx.fillStyle = colorToggle ? 'rgba(60,60,120,0.7)' : 'rgba(80,80,80,0.7)';
        const dateLabel = isMobile
          ? new Date(Number(labels[firstIdx])).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
          : new Date(Number(labels[firstIdx])).toLocaleDateString();
        ctx.fillText(dateLabel, xMid, chartArea.bottom + 4);
      }
      colorToggle = !colorToggle;
    });
    ctx.restore();
  }
});

const normalizeUnixSeconds = (value) => {
  const nowSec = Math.floor(Date.now() / 1000);
  let ts = parseInt(value, 10);
  if (isNaN(ts)) return null;
  if (ts > 1_000_000_000_000) ts = Math.floor(ts / 1000);
  if (ts > nowSec + 86400 || ts < 0) return null;
  return ts;
};


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
  , zoomPlugin
);

function DashboardMain({
  serials, selectedSerial, setSelectedSerial, data, setData, loading, setLoading, error, setError, selectedSpecies, setSelectedSpecies, mode, setMode, enabledSerials, setEnabledSerials, uniqueSerials, filteredData, uniqueSpecies, filteredSpeciesData, chartRef, chartData, chartOptions, toggleSerial, analysisText, setAnalysisText, trafficLights, plantNames, metadataLoading, editingSerial, editingName, setEditingName, handleEditPlantName, handleSavePlantName, handleCancelEdit, tempCorrection, setTempCorrection, onDevModeRequest, isMobile, deviceFirmware, deviceFirmwareLoading
}) {
  const [searchParams] = useSearchParams();

  // Pre-select serial from URL param (?serial=XXXX) on mount
  useEffect(() => {
    const serial = searchParams.get('serial');
    if (serial) {
      setEnabledSerials(new Set([serial.toUpperCase()]));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to top of page when chart first appears (but not while editing a plant name)
  useEffect(() => {
    if (chartData && !editingSerial) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [chartData, editingSerial]);

  // Download CSV for a specific serial (Developer mode, desktop only)
  const handleDownloadCSV = (serial) => {
    // Fetch corrected data for the specific serial
    if (editingSerial) return; // Prevent download while editing plant name
    console.log(`Download clicked for serial: ${serial}`);
    const normSerial = String(serial).trim().toUpperCase();
    fetch(`${API_BASE_URL}/dev/api/data-corrected/${serial}`)
      .then(res => res.json())
      .then(rows => {
        // Normalize and filter just in case
        rows = rows.filter(row => String(row.serial).trim().toUpperCase() === normSerial);
        console.log(`Rows found for serial ${serial}:`, rows.length, rows);
        if (rows.length === 0) {
          alert('No data available for this serial');
          return;
        }
        // Get all unique keys from all rows, excluding lat/long
        const allKeys = Array.from(new Set(rows.flatMap(row => Object.keys(row)))).filter(key => !['latitude', 'longitude', 'lat', 'long', 'lng'].includes(key.toLowerCase()));
        // Build CSV header
        const header = allKeys.join(',');
        // Build CSV rows
        const csvRows = rows.map(row =>
          allKeys.map(key => {
            let val = row[key];
            if (val === undefined || val === null) return '';
            // Escape quotes and commas
            if (typeof val === 'string') val = '"' + val.replace(/"/g, '""') + '"';
            return val;
          }).join(',')
        );
        const csvContent = [header, ...csvRows].join('\n');
        // Create blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${serial}_data.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(err => {
        console.error('Download fetch error:', err);
        alert('Error downloading data');
      });
  };

  return (
    <div className="App" style={{ paddingBottom: '140px' }}>
      <header className="App-header">
        <h1>Canopy Sensor Data</h1>
        
        {/* Mode switch - hidden on mobile (both modes) */}
        {!isMobile && (
          <div className="mode-switch" style={{
            position: 'absolute',
            right: '1rem',
            top: '1rem',
            zIndex: 99
          }}>
            <label style={{marginRight: 8}}>
              <input type="radio" name="mode" value="Developer" checked={mode==='Developer'} onChange={onDevModeRequest} /> Developer
            </label>
            <label>
              <input type="radio" name="mode" value="Public" checked={mode==='Public'} onChange={() => setMode('Public')} /> Public
            </label>
          </div>
        )}

        {mode === 'Developer' ? (
          // Developer mode: chart at top, then serial table with selection
          <>
            {/* Chart at top when serial is selected */}
            {chartData && (
              <div className="chart-container">
                <Line ref={chartRef} data={chartData} options={chartOptions} plugins={[dailyBackgroundPlugin(isMobile)]} />
              </div>
            )}

            {/* Correction mode and info boxes — shown once serial is selected */}
            {!loading && !error && selectedSerial && filteredData.length > 0 && (() => {
              const sorted = [...filteredData].sort((a, b) => parseInt(b.unix_timestamp, 10) - parseInt(a.unix_timestamp, 10));
              const latest = sorted[0];
              const plantName = plantNames[selectedSerial] || 'N/A';
              const isEditing = editingSerial === selectedSerial;
              
              return (
                <>
                  {/* Correction mode */}
                  <div className="control-group" style={{ marginBottom: isMobile ? '0.75em' : '1em' }}>
                    <label style={{ marginRight: '1em', fontWeight: 'bold', display: 'block', marginBottom: isMobile ? '0.5em' : '0.25em', textAlign: isMobile ? 'left' : 'center' }}>Correction Mode:</label>
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '0.5em' : '1em', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: isMobile ? 'flex-start' : 'center', fontSize: isMobile ? '0.9em' : '1em' }}>
                      <label style={{ textAlign: 'left' }}>
                        <input type="radio" name="tempCorrection" value="none" checked={tempCorrection === 'none'} onChange={() => setTempCorrection('none')} /> None
                      </label>
                      <label style={{ textAlign: 'left' }}>
                        <input type="radio" name="tempCorrection" value="temp" checked={tempCorrection === 'temp'} onChange={() => setTempCorrection('temp')} /> Temp Only
                      </label>
                      <label style={{ textAlign: 'left' }}>
                        <input type="radio" name="tempCorrection" value="temp+bump" checked={tempCorrection === 'temp+bump'} onChange={() => setTempCorrection('temp+bump')} /> Temp + Bump
                      </label>
                    </div>
                  </div>

                  {/* Info boxes */}
                  <div className="info-box-container" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-lg)', justifyContent: 'center', marginBottom: '1em' }}>
                    <div style={{ border: '1px solid #444', borderRadius: 8, padding: '0.5em 1.5em', background: '#282c34', color: 'white', minWidth: 180, flex: '1 1 auto' }}>
                      <strong>Most Recent Temperature</strong><br />
                      {latest.temperature_c !== undefined ? `${latest.temperature_c} °C` : 'N/A'}
                    </div>
                    <div style={{ border: '1px solid #444', borderRadius: 8, padding: '0.5em 1.5em', background: '#282c34', color: 'white', minWidth: 180, flex: '1 1 auto' }}>
                      <strong>Zipcode</strong><br />
                      {latest.zipcode !== undefined ? latest.zipcode : 'N/A'}
                    </div>
                    <div style={{ border: '1px solid #444', borderRadius: 8, padding: '0.5em 1.5em', background: '#282c34', color: 'white', minWidth: 180, flex: '1 1 auto' }}>
                      <strong>Most Recent Data Point</strong><br />
                      {(() => {
                        const ts = parseInt(latest.unix_timestamp, 10);
                        const nowSec = Date.now() / 1000;
                        const diffSec = Math.floor(nowSec - ts);
                        let relative;
                        if (diffSec < 3600) relative = `${Math.floor(diffSec / 60)} min ago`;
                        else if (diffSec < 86400) relative = `${Math.floor(diffSec / 3600)} hrs ago`;
                        else relative = `${Math.floor(diffSec / 86400)} days ago`;
                        const dt = new Date(ts * 1000);
                        const absolute = dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
                        return <><span style={{ fontSize: '1.05em' }}>{relative}</span><br /><span style={{ fontSize: '0.85em', color: '#aaa' }}>{absolute}</span></>;
                      })()}
                    </div>
                    <div style={{ border: '1px solid #444', borderRadius: 8, padding: '0.5em 1.5em', background: '#282c34', color: 'white', minWidth: 180, flex: '1 1 auto' }}>
                      <strong>Projected Next Data Point</strong><br />
                      {(() => {
                        const projectedTs = parseInt(latest.unix_timestamp, 10) + 86400;
                        const nowSec = Date.now() / 1000;
                        const diffSec = Math.floor(projectedTs - nowSec);
                        if (diffSec < 0) {
                          const overdueSec = -diffSec;
                          const hrs = Math.floor(overdueSec / 3600);
                          const min = Math.floor((overdueSec % 3600) / 60);
                          const label = hrs > 0 ? `${hrs} hr${hrs !== 1 ? 's' : ''} ${min} min` : `${min} min`;
                          return <span style={{ color: '#c0392b' }}>overdue by {label}</span>;
                        }
                        const dt = new Date(projectedTs * 1000);
                        const absolute = dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
                        let relative;
                        if (diffSec < 3600) relative = `in ${Math.floor(diffSec / 60)} min`;
                        else if (diffSec < 86400) relative = `in ${Math.floor(diffSec / 3600)} hrs`;
                        else relative = `in ${Math.floor(diffSec / 86400)} days`;
                        return <><span style={{ fontSize: '1.05em' }}>{relative}</span><br /><span style={{ fontSize: '0.85em', color: '#aaa' }}>{absolute}</span></>;
                      })()}
                    </div>
                    <div style={{ border: '1px solid #444', borderRadius: 8, padding: '0.5em 1.5em', background: '#282c34', color: 'white', minWidth: 180, flex: '1 1 auto' }}>
                      <strong>Firmware Version</strong><br />
                      {deviceFirmwareLoading
                        ? <span style={{ color: '#aaa' }}>Loading...</span>
                        : deviceFirmware && deviceFirmware.version
                          ? <>
                              <span style={{ fontSize: '1.05em' }}>{deviceFirmware.version}</span><br />
                              {deviceFirmware.last_check && (
                                <>
                                  {(() => {
                                    const ts = deviceFirmware.last_check;
                                    const dt = new Date(ts * 1000);
                                    const absolute = dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
                                    return <><span style={{ fontSize: '0.85em', color: '#aaa' }}>Last check-in:</span><br /><span style={{ fontSize: '0.85em', color: '#aaa' }}>{absolute}</span></>;
                                  })()}
                                </>
                              )}
                            </>
                          : <span style={{ color: '#aaa' }}>N/A</span>
                      }
                    </div>
                    <div style={{ border: '1px solid #444', borderRadius: 8, padding: '0.5em 1.5em', background: '#282c34', color: 'white', minWidth: 180, flex: '1 1 auto' }}>
                      <strong>Plant Name</strong><br />
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em', alignItems: 'center', marginTop: '0.5em' }}>
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            style={{
                              padding: '0.35em',
                              borderRadius: 4,
                              border: '1px solid #61dafb',
                              background: '#1e1e1e',
                              color: '#61dafb',
                              width: '140px',
                              textAlign: 'center',
                              fontSize: '0.9em'
                            }}
                            autoFocus
                          />
                          <div style={{ display: 'flex', gap: '0.5em', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleSavePlantName(selectedSerial)}
                              style={{
                                padding: '0.25em 0.75em',
                                fontSize: '0.8em',
                                borderRadius: 4,
                                border: '1px solid #27ae60',
                                background: '#1e1e1e',
                                color: '#27ae60',
                                cursor: 'pointer'
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              style={{
                                padding: '0.25em 0.75em',
                                fontSize: '0.8em',
                                borderRadius: 4,
                                border: '1px solid #e74c3c',
                                background: '#1e1e1e',
                                color: '#e74c3c',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={(e) => {
                            e.preventDefault();
                            handleEditPlantName(selectedSerial, plantName);
                          }}
                          style={{
                            cursor: 'pointer',
                            color: '#61dafb',
                            marginTop: '0.25em'
                          }}
                          title="Click to edit plant name"
                        >
                          {plantName}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Developer Mode Serial Selection Table */}
            <div className="developer-mode-table" style={{ display: 'flex', flexDirection: 'column', gap: '0.2em', marginBottom: '1em', justifyContent: 'center', alignItems: 'center' }}>
              {/* Header Row */}
              <div className="table-header-row" style={{ display: 'flex', flexDirection: 'row', gap: '2em', width: '100%', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold' }}>
                <div style={{ width: 160, textAlign: 'center', whiteSpace: 'nowrap' }}>Select</div>
                <div style={{ width: 160, textAlign: 'center', whiteSpace: 'nowrap' }}>Serial Numbers</div>
                <div style={{ width: 180, textAlign: 'center', whiteSpace: 'nowrap' }}>Plant Name</div>
                <div style={{ width: 240, textAlign: 'center', whiteSpace: 'nowrap' }}>Status</div>
                {!isMobile && <div style={{ width: 160, textAlign: 'center', whiteSpace: 'nowrap' }}>Download</div>}
              </div>
              {/* Data Rows */}
              {uniqueSerials.map((serial) => {
                const plantName = plantNames[serial] || 'N/A';
                let redFill = '#bbb', yellowFill = '#bbb', greenFill = '#bbb';
                const trafficLight = trafficLights[serial];
                if (trafficLight) {
                  if (trafficLight.color === 'red') {
                    redFill = '#e74c3c';
                  } else if (trafficLight.color === 'yellow') {
                    yellowFill = '#f1c40f';
                  } else if (trafficLight.color === 'green') {
                    greenFill = '#27ae60';
                  }
                }
                
                // Mobile layout: all elements on one line
                if (isMobile) {
                  return (
                    <div className="table-data-row-mobile" key={serial} style={{ display: 'flex', flexDirection: 'row', gap: '0.75em', width: '100%', justifyContent: 'space-between', alignItems: 'center', padding: '0.75em', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                      {/* Checkbox - Left */}
                      <input
                        type="checkbox"
                        checked={selectedSerial === serial}
                        onChange={() => {
                          if (selectedSerial === serial) {
                            setSelectedSerial('');
                            setSelectedSpecies('');
                          } else {
                            setSelectedSerial(serial);
                            setSelectedSpecies('');
                          }
                        }}
                        title={selectedSerial === serial ? 'Selected' : 'Not selected'}
                        style={{ cursor: 'pointer', width: 20, height: 20, flexShrink: 0 }}
                      />
                      {/* Plant Name - Middle (with truncation) */}
                      {editingSerial === serial ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em', alignItems: 'center', flex: 1, minWidth: 0 }}>
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            style={{
                              padding: '0.35em',
                              borderRadius: 4,
                              border: '1px solid #61dafb',
                              background: '#282c34',
                              color: '#61dafb',
                              width: '100%',
                              maxWidth: '140px',
                              textAlign: 'center'
                            }}
                            autoFocus
                          />
                          <div style={{ display: 'flex', gap: '0.5em' }}>
                            <button
                              onClick={() => handleSavePlantName(serial)}
                              style={{
                                padding: '0.25em 0.75em',
                                fontSize: '0.85em',
                                borderRadius: 4,
                                border: '1px solid #27ae60',
                                background: '#282c34',
                                color: '#27ae60',
                                cursor: 'pointer'
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              style={{
                                padding: '0.25em 0.75em',
                                fontSize: '0.85em',
                                borderRadius: 4,
                                border: '1px solid #e74c3c',
                                background: '#282c34',
                                color: '#e74c3c',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="plant-name"
                          onClick={() => editingSerial ? null : handleEditPlantName(serial, plantName)}
                          style={{
                            cursor: editingSerial ? 'default' : 'pointer',
                            color: selectedSerial === serial ? '#61dafb' : '#999',
                            padding: '0.5em',
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            pointerEvents: editingSerial ? 'none' : 'auto'
                          }}
                          title={plantName}
                        >
                          {plantName}
                        </div>
                      )}
                      {/* Status Light - Right (compact) */}
                      <div style={{ flexShrink: 0 }}>
                        <svg width="60" height="25" viewBox="2 5 92 30">
                          <rect x="4" y="8" width="88" height="24" rx="12" fill="#444" stroke="#222" strokeWidth="3" />
                          <circle cx="24" cy="20" r="8" fill={redFill} stroke="#888" strokeWidth="2" />
                          <circle cx="48" cy="20" r="8" fill={yellowFill} stroke="#888" strokeWidth="2" />
                          <circle cx="72" cy="20" r="8" fill={greenFill} stroke="#888" strokeWidth="2" />
                        </svg>
                      </div>
                    </div>
                  );
                }
                
                // Desktop layout: horizontal row with fixed widths
                return (
                  <div className="table-data-row" key={serial} style={{ display: 'flex', flexDirection: 'row', gap: '2em', width: '100%', justifyContent: 'center', alignItems: 'center', minHeight: 36 }}>
                    <div style={{ width: 160, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedSerial === serial}
                        onChange={() => {
                          if (selectedSerial === serial) {
                            setSelectedSerial('');
                            setSelectedSpecies('');
                          } else {
                            setSelectedSerial(serial);
                            setSelectedSpecies('');
                          }
                        }}
                        title={selectedSerial === serial ? 'Selected' : 'Not selected'}
                        style={{ cursor: 'pointer', width: 20, height: 20 }}
                      />
                    </div>
                    <div style={{ width: 160, textAlign: 'center' }}>{serial}</div>
                    <div style={{ width: 180, textAlign: 'center' }}>
                      {editingSerial === serial ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            style={{
                              padding: '0.35em',
                              borderRadius: 4,
                              border: '1px solid #61dafb',
                              background: '#282c34',
                              color: '#61dafb',
                              width: '140px',
                              textAlign: 'center'
                            }}
                            autoFocus
                          />
                          <div style={{ display: 'flex', gap: '0.5em' }}>
                            <button
                              onClick={() => handleSavePlantName(serial)}
                              style={{
                                padding: '0.25em 0.75em',
                                fontSize: '0.85em',
                                borderRadius: 4,
                                border: '1px solid #27ae60',
                                background: '#282c34',
                                color: '#27ae60',
                                cursor: 'pointer'
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              style={{
                                padding: '0.25em 0.75em',
                                fontSize: '0.85em',
                                borderRadius: 4,
                                border: '1px solid #e74c3c',
                                background: '#282c34',
                                color: '#e74c3c',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="plant-name"
                          onClick={() => handleEditPlantName(serial, plantName)}
                          style={{
                            cursor: 'pointer',
                            color: selectedSerial === serial ? '#61dafb' : '#999',
                            padding: '0.5em'
                          }}
                          title="Click to edit plant name"
                        >
                          {plantName}
                        </div>
                      )}
                    </div>
                    <div style={{ width: 240, textAlign: 'center' }}>
                      <svg width="96" height="40" viewBox="2 5 92 30">
                        <rect x="4" y="8" width="88" height="24" rx="12" fill="#444" stroke="#222" strokeWidth="3" />
                        <circle cx="24" cy="20" r="8" fill={redFill} stroke="#888" strokeWidth="2" />
                        <circle cx="48" cy="20" r="8" fill={yellowFill} stroke="#888" strokeWidth="2" />
                        <circle cx="72" cy="20" r="8" fill={greenFill} stroke="#888" strokeWidth="2" />
                      </svg>
                    </div>
                    <div style={{ width: 160, textAlign: 'center' }}>
                      <button
                        onClick={() => handleDownloadCSV(serial)}
                        style={{
                          padding: '0.5em 1em',
                          borderRadius: 6,
                          border: '1px solid #27ae60',
                          background: '#282c34',
                          color: '#27ae60',
                          cursor: 'pointer',
                          fontSize: '0.9em',
                          fontWeight: 'bold'
                        }}
                      >
                        Download Now
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Developer Mode Serial Selection Table ends here */}
          </>
        ) : (
          // Public mode: chart at top, then serial table, then map at bottom
          <>
            {enabledSerials.size > 0 && chartData && (
              <div className="chart-container">
                <Line ref={chartRef} data={chartData} options={chartOptions} plugins={[dailyBackgroundPlugin(isMobile)]} />
              </div>
            )}
            <div className="public-mode-table" style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '1em' : '0.2em', marginBottom: '1em', justifyContent: 'center', alignItems: 'center' }}>
                {/* Header Row - Hide on mobile */}
                {!isMobile && (
                  <div className="table-header-row" style={{ display: 'flex', flexDirection: 'row', gap: '2em', width: '100%', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold' }}>
                    <div style={{ width: 160, textAlign: 'center', whiteSpace: 'nowrap' }}>Graph?</div>
                    <div style={{ width: 180, textAlign: 'center', whiteSpace: 'nowrap' }}>Plant Name</div>
                    <div style={{ width: 240, textAlign: 'center', whiteSpace: 'nowrap' }}>Status {metadataLoading && <span style={{marginLeft: '0.5em', animation: 'pulse 1.5s infinite'}}>●</span>}</div>
                  </div>
                )}
            {/* Data Rows */}
            {uniqueSerials.map((serial, idx) => {
              // Get the plant name from the plantNames state
              const plantName = plantNames[serial] || 'N/A';
              
              // Determine bulb colors from traffic light data or defaults
              let redFill = '#bbb', yellowFill = '#bbb', greenFill = '#bbb';
              const trafficLight = trafficLights[serial];
              
              if (trafficLight) {
                // Use live traffic light color from MongoDB
                if (trafficLight.color === 'red') {
                  redFill = '#e74c3c';
                } else if (trafficLight.color === 'yellow') {
                  yellowFill = '#f1c40f';
                } else if (trafficLight.color === 'green') {
                  greenFill = '#27ae60';
                }
              }
              // If no traffic light data, all lights remain grey (#bbb)
              
              // Mobile layout: all elements (checkbox, name, traffic light) on one line
              if (isMobile) {
                return (
                  <div className="table-data-row-mobile" key={serial} style={{ display: 'flex', flexDirection: 'row', gap: '0.75em', width: '100%', justifyContent: 'space-between', alignItems: 'center', padding: '0.75em', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                    {/* Checkbox - Left */}
                    <input
                      type="checkbox"
                      checked={enabledSerials.has(serial)}
                      onChange={() => setEnabledSerials(enabledSerials.has(serial) ? new Set() : new Set([serial]))}
                      title={enabledSerials.has(serial) ? 'Enabled' : 'Disabled'}
                      style={{ cursor: 'pointer', width: 20, height: 20, flexShrink: 0 }}
                    />
                    {/* Plant Name - Middle (with truncation) */}
                    {editingSerial === serial ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em', alignItems: 'center', flex: 1, minWidth: 0 }}>
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          style={{
                            padding: '0.35em',
                            borderRadius: 4,
                            border: '1px solid #61dafb',
                            background: '#282c34',
                            color: '#61dafb',
                            width: '100%',
                            maxWidth: '140px',
                            textAlign: 'center'
                          }}
                          autoFocus
                        />
                        <div style={{ display: 'flex', gap: '0.5em' }}>
                          <button
                            onClick={() => handleSavePlantName(serial)}
                            style={{
                              padding: '0.25em 0.75em',
                              fontSize: '0.85em',
                              borderRadius: 4,
                              border: '1px solid #27ae60',
                              background: '#282c34',
                              color: '#27ae60',
                              cursor: 'pointer'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            style={{
                              padding: '0.25em 0.75em',
                              fontSize: '0.85em',
                              borderRadius: 4,
                              border: '1px solid #e74c3c',
                              background: '#282c34',
                              color: '#e74c3c',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="plant-name"
                        style={{
                          cursor: 'default',
                          padding: '0.5em',
                          flex: 1,
                          textAlign: 'left',
                          fontWeight: 'bold',
                          color: '#333',
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={plantName}
                      >
                        {plantName}
                      </div>
                    )}
                    {/* Status Light - Right (compact) */}
                    <div style={{ flexShrink: 0 }}>
                      <svg width="60" height="25" viewBox="2 5 92 30">
                        <rect x="4" y="8" width="88" height="24" rx="12" fill="#444" stroke="#222" strokeWidth="3" />
                        <circle cx="24" cy="20" r="8" fill={redFill} stroke="#888" strokeWidth="2" />
                        <circle cx="48" cy="20" r="8" fill={yellowFill} stroke="#888" strokeWidth="2" />
                        <circle cx="72" cy="20" r="8" fill={greenFill} stroke="#888" strokeWidth="2" />
                      </svg>
                    </div>
                  </div>
                );
              }
              
              // Desktop layout: horizontal row
              return (
                <div className="table-data-row" key={serial} style={{ display: 'flex', flexDirection: 'row', gap: '2em', width: '100%', justifyContent: 'center', alignItems: 'center', minHeight: 36 }}>
                  {/* Enable/Disable */}
                  <div style={{ width: 160, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={enabledSerials.has(serial)}
                      onChange={() => setEnabledSerials(enabledSerials.has(serial) ? new Set() : new Set([serial]))}
                      title={enabledSerials.has(serial) ? 'Enabled' : 'Disabled'}
                      style={{ cursor: 'pointer', width: 20, height: 20 }}
                    />
                  </div>
                  {/* Plant Name */}
                  <div style={{ width: 180, textAlign: 'center' }}>
                    {editingSerial === serial ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          style={{
                            padding: '0.35em',
                            borderRadius: 4,
                            border: '1px solid #61dafb',
                            background: '#282c34',
                            color: '#61dafb',
                            width: '140px',
                            textAlign: 'center'
                          }}
                          autoFocus
                        />
                        <div style={{ display: 'flex', gap: '0.5em' }}>
                          <button
                            onClick={() => handleSavePlantName(serial)}
                            style={{
                              padding: '0.25em 0.75em',
                              fontSize: '0.85em',
                              borderRadius: 4,
                              border: '1px solid #27ae60',
                              background: '#282c34',
                              color: '#27ae60',
                              cursor: 'pointer'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            style={{
                              padding: '0.25em 0.75em',
                              fontSize: '0.85em',
                              borderRadius: 4,
                              border: '1px solid #e74c3c',
                              background: '#282c34',
                              color: '#e74c3c',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="plant-name"
                        style={{
                          cursor: 'default',
                          padding: '0.5em'
                        }}
                        title="Plant names are managed in Developer mode"
                      >
                        {plantName}
                      </div>
                    )}
                  </div>
                  {/* Status Light */}
                  <div style={{ width: 240, textAlign: 'center' }}>
                    <svg width="96" height="40" viewBox="2 5 92 30">
                      {/* Horizontal traffic light body */}
                      <rect x="4" y="8" width="88" height="24" rx="12" fill="#444" stroke="#222" strokeWidth="3" />
                      {/* Red bulb (left) */}
                      <circle cx="24" cy="20" r="8" fill={redFill} stroke="#888" strokeWidth="2" />
                      {/* Yellow bulb (center) */}
                      <circle cx="48" cy="20" r="8" fill={yellowFill} stroke="#888" strokeWidth="2" />
                      {/* Green bulb (right) */}
                      <circle cx="72" cy="20" r="8" fill={greenFill} stroke="#888" strokeWidth="2" />
                    </svg>
                  </div>
                </div>
              );
            })}
            </div>
          </>
        )}

        {/* Map at bottom in Developer mode */}
        {mode === 'Developer' && (
          <div style={{ marginTop: '2em', marginBottom: '2em', display: 'flex', justifyContent: 'center' }}>
            <GoogleMapWithPlant />
          </div>
        )}

        {/* Map at bottom in Public mode */}
        {mode === 'Public' && (
          <div style={{ marginTop: '2em', marginBottom: '2em', display: 'flex', justifyContent: 'center' }}>
            <GoogleMapWithPlant />
          </div>
        )}

        {/* Mode switch at bottom for mobile (both modes) */}
        {isMobile && (
          <div style={{ marginTop: '2em', padding: '1em', textAlign: 'center', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'center', gap: '1em' }}>
            <label style={{marginRight: 8}}>
              <input type="radio" name="mode" value="Developer" checked={mode==='Developer'} onChange={onDevModeRequest} /> Developer
            </label>
            <label>
              <input type="radio" name="mode" value="Public" checked={mode==='Public'} onChange={() => setMode('Public')} /> Public
            </label>
          </div>
        )}

        {loading && <p>Loading...</p>}
        {error && <p style={{ color: 'red' }}>Error: {error}</p>}
        
        {/* Analysis text display - inline within page */}
        {analysisText && (
          <div style={{
            margin: '2em 1em 1em 1em',
            background: '#1e1e1e',
            color: '#e0e0e0',
            padding: '1.5em',
            border: '2px solid #61dafb',
            borderRadius: '8px',
            maxHeight: 'none'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5em' }}>
              <strong style={{ color: '#61dafb' }}>AI Analysis</strong>
              <button
                onClick={() => setAnalysisText('')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#61dafb',
                  cursor: 'pointer',
                  fontSize: '1.2em',
                  padding: '0'
                }}
              >
                ✕
              </button>
            </div>
            <p style={{ margin: '0', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{analysisText}</p>
          </div>
        )}
        
        <br /><br /><br /><br />
      </header>
    </div>
  );
}

function App() {
  const [serials, setSerials] = useState([]);
  const [selectedSerial, setSelectedSerial] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSpecies, setSelectedSpecies] = useState('');
  const [mode, setMode] = useState('Public'); // 'Developer' or 'Public'
  const [devToken, setDevToken] = useState(() => sessionStorage.getItem('devToken') || null);
  const [showDevModal, setShowDevModal] = useState(false);
  const [devPasswordInput, setDevPasswordInput] = useState('');
  const [devAuthError, setDevAuthError] = useState('');
  const [devAuthLoading, setDevAuthLoading] = useState(false);
  const [enabledSerials, setEnabledSerials] = useState(new Set()); // For Public mode
  const [analysisText, setAnalysisText] = useState('');
  const [trafficLights, setTrafficLights] = useState({}); // Map of serial -> {color, slope, reason, updated_at}
  const [plantNames, setPlantNames] = useState({}); // Map of serial -> plant name
  const [metadataLoading, setMetadataLoading] = useState(false); // Track metadata fetch status (for lazy loading indicator)
  const [editingSerial, setEditingSerial] = useState(null); // Track which plant is being edited
  const [editingName, setEditingName] = useState('');
  const [tempCorrection, setTempCorrection] = useState('none');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [deviceFirmware, setDeviceFirmware] = useState(null);
  const [deviceFirmwareLoading, setDeviceFirmwareLoading] = useState(false);
  const fetchedSerials = useRef(new Set()); // Track which serials have been fetched to avoid re-fetching // Track the editing name value

  // Detect mobile viewport
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Remove normalization for serials, display as-is
  // const normalizeSerial = (serial) => serial.toString();

  // Serial dropdown and lists should use raw serials
  const uniqueSerials = React.useMemo(() => {
    const unique = Array.from(new Set(serials.map(s => s.toString())));
    unique.sort();
    // Only show 12-digit serials in Public mode, all serials in Developer mode
    if (mode === 'Public') {
      // Allow any 12-character alphanumeric serials
      return unique.filter(s => /^[A-Za-z0-9]{12}$/.test(s));
    }
    return unique;
  }, [serials, mode]);

  // Fetch device firmware version when a serial is selected in Developer mode
  useEffect(() => {
    if (mode !== 'Developer' || !selectedSerial) {
      setDeviceFirmware(null);
      return;
    }
    setDeviceFirmwareLoading(true);
    fetch(`${API_BASE_URL}/dev/api/device-firmware/${selectedSerial}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { setDeviceFirmware(data); setDeviceFirmwareLoading(false); })
      .catch(() => { setDeviceFirmware(null); setDeviceFirmwareLoading(false); });
  }, [selectedSerial, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch serials on mount
  // Fetch serials and all data on mount
  useEffect(() => {
    fetch(`${API_BASE_URL}/dev/api/serials`)
      .then(res => res.json())
      .then(serialList => {
        setSerials(serialList);
        // Data for each serial is fetched lazily when the user selects/enables it.
        // No bulk upfront fetch — avoids N×HTTP round-trips on page load.
        setMetadataLoading(true);
        
        Promise.all([
          fetch(`${API_BASE_URL}/api/traffic-lights`).then(res => res.json()),
          fetch(`${API_BASE_URL}/api/plant-names`).then(res => res.json())
        ])
          .then(([lights, names]) => {
            // Process traffic lights
            const lightsMap = {};
            lights.forEach(light => {
              lightsMap[light.serial] = light;
            });
            setTrafficLights(lightsMap);
            
            // Process plant names
            const namesMap = {};
            names.forEach(item => {
              namesMap[item.serial] = item.name;
            });
            setPlantNames(namesMap);
            setMetadataLoading(false); // Mark complete
          })
          .catch(err => {
            console.error('Error fetching dashboard metadata:', err);
            setMetadataLoading(false); // Mark complete even on error
          });
      })
      .catch(err => setError(err.message));
  }, []);

  // Clear data cache when switching modes
  useEffect(() => {
    console.log(`=== MODE SWITCHED TO ${mode} ===`);
    console.log('Clearing data cache to force fresh fetch');
    setData([]);
    fetchedSerials.current.clear();
  }, [mode]);

  // Lazy-load sensor data only for serials the user has selected / enabled
  useEffect(() => {
    const toFetch = mode === 'Developer'
      ? (selectedSerial && !fetchedSerials.current.has(selectedSerial) ? [selectedSerial] : [])
      : Array.from(enabledSerials).filter(s => !fetchedSerials.current.has(s));

    if (toFetch.length === 0) return;
    toFetch.forEach(s => fetchedSerials.current.add(s));
    if (mode === 'Developer') setLoading(true);

    console.log(`=== FETCHING DATA (${mode} mode) ===`);
    console.log('toFetch serials:', toFetch);

    Promise.all(
      toFetch.map(serial =>
        fetch(`${API_BASE_URL}/dev/api/data-corrected/${serial}`)
          .then(res => res.json())
          .then(data => {
            console.log(`Fetched ${serial} (${mode}):`, {
              rows: Array.isArray(data) ? data.length : data.length || 0,
              firstDate: Array.isArray(data) && data[0] ? new Date(parseInt(data[0].unix_timestamp, 10) * 1000).toISOString() : 'N/A',
              lastDate: Array.isArray(data) && data.length > 0 ? new Date(parseInt(data[data.length-1].unix_timestamp, 10) * 1000).toISOString() : 'N/A'
            });
            return data;
          })
      )
    )
      .then(results => {
        const flatResults = results.flat();
        console.log(`Total rows received (${mode}):`, flatResults.length);
        setData(prev => [...prev, ...flatResults]);
        if (mode === 'Developer') setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        if (mode === 'Developer') setLoading(false);
      });
  }, [selectedSerial, enabledSerials, mode]);

  // Normalize serials for dropdown
  const normalizedSerials = React.useMemo(() => {
    const unique = Array.from(new Set(serials.map(s => s.toString())));
    unique.sort();
    return unique;
  }, [serials]);

  // Debug: log all normalized serials found only when serials changes
  React.useEffect(() => {
    //console.log('Dropdown serials:', normalizedSerials);
  }, [normalizedSerials]);


  // Filter data by selected serial (raw)
  const filteredData = selectedSerial
    ? data.filter(row => row.serial === selectedSerial)
    : data;

  // Extract unique species for the selected serial
  const uniqueSpecies = React.useMemo(() => {
    if (filteredData.length > 0) {
      const speciesKeys = Object.keys(filteredData[0]).filter(k => k.startsWith('species'));
      if (speciesKeys.length > 0) {
        return Array.from(new Set(
          filteredData.flatMap(row => speciesKeys.map(k => row[k])).filter(Boolean)
        ));
      } else {
        return Array.from(new Set(filteredData.map((row) => row.species).filter(Boolean)));
      }
    }
    return [];
  }, [filteredData]);
  
  const filteredSpeciesData = React.useMemo(() => {
    if (!selectedSerial || !selectedSpecies) return [];
    const speciesKeys = Object.keys(filteredData[0] || {}).filter(k => k.startsWith('species'));
    
    const filtered = filteredData.filter(row =>
      speciesKeys.some(k => row[k] === selectedSpecies)
    );
    
    console.log('=== SPECIES FILTERING ===');
    console.log('filteredData length:', filteredData.length);
    console.log('After species filter:', filtered.length);
    console.log('Filtered out rows:', filteredData.length - filtered.length);
    
    // Log last few rows from filteredData to see what species they have
    if (filteredData.length > filtered.length) {
      const lastFilteredIdx = filteredData.findIndex(row => !speciesKeys.some(k => row[k] === selectedSpecies));
      if (lastFilteredIdx > 0) {
        console.log('First row that was filtered out:');
        const sampleRow = filteredData[lastFilteredIdx];
        console.log({
          ts: sampleRow.unix_timestamp,
          date: new Date(parseInt(sampleRow.unix_timestamp, 10) * 1000).toISOString(),
          species0: sampleRow.species0,
          species1: sampleRow.species1,
          species2: sampleRow.species2,
          species3: sampleRow.species3,
          selectedSpecies
        });
      }
    }
    
    return filtered;
  }, [filteredData, selectedSerial, selectedSpecies]);  
  
  // Debug: inspect the data used to build the chart
  console.log('data (raw fetched):', data);
  console.log('selectedSerial:', selectedSerial, 'selectedSpecies:', selectedSpecies);
  console.log('normalizedSerials:', normalizedSerials);
  console.log('uniqueSpecies:', uniqueSpecies);
  console.log('filteredSpeciesData:', filteredSpeciesData);

  const chartRef = useRef(null);

  // Helper function to apply moving average smoothing to data
  const applyMovingAverage = (data, windowSize = 3) => {
    if (data.length <= windowSize) return data;
    const smoothed = [];
    for (let i = 0; i < data.length; i++) {
      const start = Math.max(0, i - Math.floor(windowSize / 2));
      const end = Math.min(data.length, i + Math.floor(windowSize / 2) + 1);
      const window = data.slice(start, end).filter(v => v !== null && v !== undefined);
      if (window.length > 0) {
        smoothed.push(window.reduce((a, b) => a + b) / window.length);
      } else {
        smoothed.push(data[i]);
      }
    }
    return smoothed;
  };

  // Auto-select first species in both Public and Developer modes
  useEffect(() => {
    if (selectedSerial && filteredData.length > 0) {
      // Select the species from the FIRST ROW of data (earliest timestamp), matching Public mode behavior
      const speciesKeys = Object.keys(filteredData[0]).filter(k => k.startsWith('species'));
      const firstSpecies = speciesKeys.length > 0 ? filteredData[0][speciesKeys[0]] : filteredData[0].species;
      setSelectedSpecies(firstSpecies);
    }
  }, [selectedSerial, filteredData]);

  // ============================================================================
  // MAIN chartData MEMO: Build chart data directly from timestamps
  // ============================================================================
  // STRATEGY: Use actual data points as-is. Sort by timestamp, extract values.
  // Real gaps (offline periods) appear as visual breaks naturally. No synthetic
  // nulls or artificial grids—the data speaks for itself.
  // ============================================================================
  const chartData = React.useMemo(() => {
    if (mode === 'Developer') {
      // =====================================================================
      // DEVELOPER MODE: Single serial, single species
      // =====================================================================
      if (!selectedSerial || !selectedSpecies || filteredData.length === 0) return null;

      // Determine which sensor index to use based on selectedSpecies
      const speciesKeys = Object.keys(filteredData[0]).filter(k => k.startsWith('species'));
      const speciesIndex = speciesKeys.findIndex(k => filteredData[0][k] === selectedSpecies);
      if (speciesIndex === -1) return null;

      const correctedKey = `sensor${speciesIndex}_corrected`;
      const normalizedKey = `sensor${speciesIndex}_normalized`;
      const rawKey = `sensor${speciesIndex}`;

      // Sort data by timestamp
      const sortedData = [...filteredData].sort((a, b) => 
        normalizeUnixSeconds(a.unix_timestamp) - normalizeUnixSeconds(b.unix_timestamp)
      );

      // Deduplicate timestamps using a Set (keep first occurrence per timestamp)
      const seenTimestamps = new Set();
      const labels = [];
      const values = [];

      sortedData.forEach(row => {
        const ts = normalizeUnixSeconds(row.unix_timestamp);
        if (ts !== null && !seenTimestamps.has(ts)) {
          seenTimestamps.add(ts);
          labels.push(ts * 1000); // Convert to milliseconds for Chart.js
          
          let val = row[rawKey];
          if (tempCorrection === 'temp+bump') {
            val = row[correctedKey] !== undefined ? row[correctedKey] : val;
          } else if (tempCorrection === 'temp') {
            val = row[normalizedKey] !== undefined ? row[normalizedKey] : val;
          }
          values.push(val ?? null);
        }
      });

      if (labels.length === 0) return null;

      return {
        labels,
        datasets: [
          {
            label: plantNames[selectedSerial] || selectedSerial,
            data: values,
            borderColor: 'rgba(75,192,192,1)',
            backgroundColor: 'rgba(75,192,192,0.2)',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 5,
            fill: false,
            spanGaps: true,
          }
        ]
      };
    } else {
      // =====================================================================
      // PUBLIC MODE: Multiple serials, auto-selected species each
      // =====================================================================
      if (enabledSerials.size === 0) return null;

      const colors = [
        { border: 'rgba(75,192,192,1)', bg: 'rgba(75,192,192,0.2)' },
        { border: 'rgba(255,99,132,1)', bg: 'rgba(255,99,132,0.2)' },
        { border: 'rgba(54,162,235,1)', bg: 'rgba(54,162,235,0.2)' },
        { border: 'rgba(255,206,86,1)', bg: 'rgba(255,206,86,0.2)' },
        { border: 'rgba(153,102,255,1)', bg: 'rgba(153,102,255,0.2)' },
        { border: 'rgba(255,159,64,1)', bg: 'rgba(255,159,64,0.2)' },
      ];

      // Collect all unique timestamps across all enabled serials
      const allLabels = new Set();
      const serialDataMap = new Map();

      const now = Date.now();
      const sevenDaysAgoSec = Math.floor((now - 7 * 24 * 60 * 60 * 1000) / 1000);

      Array.from(enabledSerials).forEach((serial) => {
        let serialData = data.filter(row => row.serial === serial);
        if (serialData.length === 0) return;

        // On mobile public mode, filter to last 7 days
        if (isMobile) {
          serialData = serialData.filter(row => {
            const ts = normalizeUnixSeconds(row.unix_timestamp);
            return ts !== null && ts >= sevenDaysAgoSec;
          });
        }

        // Auto-select first species
        const speciesKeys = Object.keys(serialData[0] || {}).filter(k => k.startsWith('species'));
        const firstSpecies = speciesKeys.length > 0 ? serialData[0][speciesKeys[0]] : serialData[0].species;
        if (!firstSpecies) return;

        const speciesIndex = speciesKeys.findIndex(k => serialData[0][k] === firstSpecies);
        const idx = speciesIndex !== -1 ? speciesIndex : 0;
        const correctedKey = `sensor${idx}_corrected`;
        const normalizedKey = `sensor${idx}_normalized`;
        const rawKey = `sensor${idx}`;

        // Sort by timestamp and collect labels + values
        const sorted = [...serialData].sort((a, b) =>
          normalizeUnixSeconds(a.unix_timestamp) - normalizeUnixSeconds(b.unix_timestamp)
        );

        const serialLabels = [];
        const serialValues = [];

        sorted.forEach(row => {
          const ts = normalizeUnixSeconds(row.unix_timestamp);
          if (ts !== null) {
            const tsMs = ts * 1000;
            serialLabels.push(tsMs);
            allLabels.add(tsMs);

            const val = row[correctedKey] !== undefined ? row[correctedKey]
              : (row[normalizedKey] !== undefined ? row[normalizedKey] : row[rawKey]);
            serialValues.push(val);
          }
        });

        serialDataMap.set(serial, { labels: serialLabels, values: serialValues });
      });

      if (allLabels.size === 0) return null;

      const labels = Array.from(allLabels).sort((a, b) => a - b);

      // Build datasets - each serial mapped to the full label array
      const datasets = [];
      Array.from(enabledSerials).forEach((serial, index) => {
        if (!serialDataMap.has(serial)) return;

        const { labels: serialLabels, values: serialValues } = serialDataMap.get(serial);
        const color = colors[index % colors.length];

        // Create a map of label → value for this serial
        const labelToValueMap = new Map();
        serialLabels.forEach((label, idx) => {
          labelToValueMap.set(label, serialValues[idx]);
        });

        // Align to full label array (null where serial has no data)
        const alignedValues = labels.map(label => labelToValueMap.get(label) ?? null);

        // Normalize: subtract first non-null value
        const firstValue = alignedValues.find(v => v !== null && v !== undefined);
        const normalizedValues = alignedValues.map(v =>
          v === null || v === undefined || firstValue === undefined ? null : v - firstValue
        );

        // Apply smoothing
        let smoothingWindow = 0;
        if (isMobile) {
          smoothingWindow = 7;
        }
        const finalValues = smoothingWindow > 0 ? applyMovingAverage(normalizedValues, smoothingWindow) : normalizedValues;

        const plantName = plantNames[serial] || `Serial ${serial}`;
        datasets.push({
          label: plantName,
          data: finalValues,
          borderColor: color.border,
          backgroundColor: color.bg,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          fill: false,
          spanGaps: true,
        });
      });

      return datasets.length > 0 ? { labels, datasets } : null;
    }
  }, [mode, filteredData, selectedSerial, selectedSpecies, enabledSerials, data, tempCorrection, isMobile, plantNames]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 28 // Add extra space for date labels
      }
    },
    plugins: {
      legend: { display: true },
      title: { display: !isMobile, text: 'Dendrometer Data' },
      tooltip: {
        mode: 'nearest',
        intersect: false,
        callbacks: {
          title: (items) => {
            const label = items?.[0]?.label;
            const ms = typeof label === 'number' ? label : Number(label);
            if (!Number.isNaN(ms)) return new Date(ms).toLocaleString();
            return label ?? '';
          },
          label: (item) => {
            const val = item.raw;
            return `Value: ${val !== null && val !== undefined ? val.toFixed(2) : 'N/A'}`;
          }
        }
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x'
        },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          drag: { enabled: true },
          mode: 'x'
        }
      }
    },
    scales: {
      x: {
        display: false, // Hide x-axis labels
        title: { display: false }
      },
      y: { title: { display: !isMobile, text: isMobile ? '' : 'Value' } }
    }
  };

  const handleDevModeRequest = () => {
    if (devToken) {
      setMode('Developer');
    } else {
      setShowDevModal(true);
    }
  };

  const handleDevAuth = async () => {
    setDevAuthLoading(true);
    setDevAuthError('');
    try {
      const res = await fetch(`${API_BASE_URL}/dev/api/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: devPasswordInput })
      });
      const json = await res.json();
      if (res.ok && json.token) {
        sessionStorage.setItem('devToken', json.token);
        setDevToken(json.token);
        setShowDevModal(false);
        setDevPasswordInput('');
        setMode('Developer');
      } else {
        setDevAuthError(json.error || 'Invalid password');
      }
    } catch {
      setDevAuthError('Network error');
    } finally {
      setDevAuthLoading(false);
    }
  };

  const handleEditPlantName = (serial, currentName) => {
    setEditingSerial(serial);
    setEditingName(currentName);
  };

  const handleSavePlantName = async (serial) => {
    if (!editingName.trim()) {
      alert('Plant name cannot be empty');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/plant-names/${serial}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingName })
      });

      const result = await response.json();
      if (result.success) {
        // Update local state with new name
        setPlantNames(prev => ({
          ...prev,
          [serial]: editingName
        }));
        setEditingSerial(null);
        setEditingName('');
      } else {
        alert('Failed to update plant name: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving plant name:', error);
      alert('Failed to save plant name');
    }
  };

  const handleCancelEdit = () => {
    setEditingSerial(null);
    setEditingName('');
  };

  const toggleSerial = (serial) => {
    setEnabledSerials(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serial)) {
        newSet.delete(serial);
      } else {
        newSet.add(serial);
      }
      return newSet;
    });
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/dashboard" element={
          <DashboardMain
            serials={serials}
            selectedSerial={selectedSerial}
            setSelectedSerial={setSelectedSerial}
            data={data}
            setData={setData}
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
            selectedSpecies={selectedSpecies}
            setSelectedSpecies={setSelectedSpecies}
            mode={mode}
            setMode={setMode}
            enabledSerials={enabledSerials}
            setEnabledSerials={setEnabledSerials}
            uniqueSerials={uniqueSerials}
            filteredData={filteredData}
            uniqueSpecies={uniqueSpecies}
            filteredSpeciesData={filteredSpeciesData}
            chartRef={chartRef}
            chartData={chartData}
            chartOptions={chartOptions}
            toggleSerial={toggleSerial}
            analysisText={analysisText}
            setAnalysisText={setAnalysisText}
            trafficLights={trafficLights}
            plantNames={plantNames}
            metadataLoading={metadataLoading}
            editingSerial={editingSerial}
            editingName={editingName}
            setEditingName={setEditingName}
            handleEditPlantName={handleEditPlantName}
            handleSavePlantName={handleSavePlantName}
            handleCancelEdit={handleCancelEdit}
            tempCorrection={tempCorrection}
            setTempCorrection={setTempCorrection}
            onDevModeRequest={handleDevModeRequest}
            isMobile={isMobile}
            deviceFirmware={deviceFirmware}
            deviceFirmwareLoading={deviceFirmwareLoading}
          />
        } />
        <Route path="/clip-install" element={<ClipInstall />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/dendrometer-fundamentals" element={<DendrometerFundamentals />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />
      </Routes>

      {showDevModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#282c34', border: '1px solid #61dafb', borderRadius: 8, padding: '2em', minWidth: 320, color: 'white' }}>
            <h2 style={{ marginTop: 0, color: '#61dafb' }}>Developer Access</h2>
            <input
              type="password"
              value={devPasswordInput}
              onChange={e => setDevPasswordInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !devAuthLoading && handleDevAuth()}
              placeholder="Password"
              style={{ width: '100%', padding: '0.5em', borderRadius: 4, border: '1px solid #61dafb', background: '#1e1e1e', color: 'white', marginBottom: '0.75em', boxSizing: 'border-box' }}
              autoFocus
            />
            {devAuthError && <p style={{ color: '#e74c3c', margin: '0 0 0.75em' }}>{devAuthError}</p>}
            <div style={{ display: 'flex', gap: '1em', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowDevModal(false); setDevPasswordInput(''); setDevAuthError(''); }}
                style={{ padding: '0.5em 1em', borderRadius: 4, border: '1px solid #aaa', background: 'transparent', color: '#aaa', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDevAuth}
                disabled={devAuthLoading}
                style={{ padding: '0.5em 1em', borderRadius: 4, border: '1px solid #61dafb', background: '#61dafb', color: '#282c34', cursor: 'pointer', fontWeight: 'bold' }}
              >
                {devAuthLoading ? 'Checking...' : 'Unlock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Router>
  );
}

export default App;
